import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { modulesAPI, certificationAPI, promotionAPI, partnerTrackAPI } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Landmark,
  Target,
  ClipboardList,
  Search,
  UserSearch,
  ClipboardCheck,
  Presentation,
  CalendarCheck,
  Handshake,
  HeartHandshake,
  Shield,
  ChevronRight,
  Award,
  BookOpen,
  FileText,
  CheckSquare,
  TrendingUp,
  Calendar,
  PhoneCall,
  UserCheck,
  Briefcase,
  Users,
  Building,
  Megaphone,
  Cpu,
  Star,
  Crown,
  Lock,
  Play,
  RotateCcw,
} from "lucide-react";

const ICON_MAP = {
  Landmark, Target, ClipboardList, Search, UserSearch, ClipboardCheck,
  Presentation, CalendarCheck, Handshake, HeartHandshake, Shield,
  TrendingUp, Calendar, PhoneCall, UserCheck, Briefcase, Users, Building, Megaphone, Cpu,
};

const LEVEL_TITLES = { 1: "Associate", 2: "Consultant", 3: "Senior Consultant", 4: "Principal", 5: "Partner" };
const READINESS_LABELS = { learning: "Learning", shadowing: "Shadowing", client_ready: "Client-Ready", fully_billable: "Fully Billable" };

const PARTNER_TRACK_STAGE_NAMES = {
  "not_eligible": "Not Eligible",
  "partner_track_entry": "Partner Track Entry",
  "principal_on_partner_track": "Principal on Partner Track",
  "partner_candidate": "Partner Candidate"
};

function getModuleProgress(progress) {
  const sections = ["learning", "sops", "checklists", "assessment"];
  const completed = sections.filter((s) => progress[s]).length;
  return Math.round((completed / sections.length) * 100);
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [certification, setCertification] = useState(null);
  const [promotionStatus, setPromotionStatus] = useState(null);
  const [partnerTrackStatus, setPartnerTrackStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [modRes, certRes] = await Promise.all([
          modulesAPI.getAll(),
          certificationAPI.get(),
        ]);
        setModules(modRes.data);
        setCertification(certRes.data);
        
        // Fetch promotion and partner track status if user is available
        if (user?.username) {
          try {
            const [promoRes, ptRes] = await Promise.all([
              promotionAPI.getStatus(user.username),
              partnerTrackAPI.getStatus(user.username)
            ]);
            setPromotionStatus(promoRes.data);
            setPartnerTrackStatus(ptRes.data);
          } catch (err) {
            console.error("Failed to load status:", err);
          }
        }
      } catch (err) {
        console.error("Failed to load dashboard:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-slate-400 text-sm uppercase tracking-wider">Loading...</div>
      </div>
    );
  }

  const totalProgress = certification
    ? certification.completion_percentage
    : 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <img
          src="https://customer-assets.emergentagent.com/job_exec-talent-hub-1/artifacts/7a127wcx_Color%20logo%20-%20no%20background.svg"
          alt="Axentia Global"
          className="h-8 mt-1 hidden sm:block"
          data-testid="dashboard-logo"
        />
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Executive Search Academy
          </h1>
          <p className="text-slate-500 mt-2 text-base font-body">
            Your path to becoming a Certified Axentia Global Executive Search Consultant.
          </p>
        </div>
      </div>

      {/* Academy Progress Bar */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-sm p-6 shadow-lg" data-testid="academy-progress">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-white font-heading text-lg font-semibold">Academy Progress</h2>
            <p className="text-slate-400 text-sm mt-1">Complete all modules to earn your certification</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-white font-heading">
              {certification?.completed_modules || 0}<span className="text-lg text-slate-400">/{certification?.total_modules || 22}</span>
            </div>
            <div className="text-xs text-slate-400">modules complete</div>
          </div>
        </div>
        <Progress value={totalProgress} className="h-2" />
        <div className="flex items-center justify-between mt-2 text-sm">
          <span className="text-slate-400">{totalProgress}% complete</span>
          {totalProgress === 100 ? (
            <button 
              onClick={() => navigate("/certification")}
              className="text-emerald-400 font-medium hover:text-emerald-300 transition-colors"
            >
              🎓 Download Certificate
            </button>
          ) : (
            <span className="text-slate-500">{Math.ceil((100 - totalProgress) / (100 / (certification?.total_modules || 22)))} modules remaining</span>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white border border-slate-200 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] rounded-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-slate-100 rounded-sm flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-slate-600" strokeWidth={1.5} />
            </div>
            <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Overall Progress</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 font-heading">{totalProgress}%</div>
          <Progress value={totalProgress} className="mt-2 h-1.5" data-testid="overall-progress" />
        </div>
        <div className="bg-white border border-slate-200 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] rounded-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-slate-100 rounded-sm flex items-center justify-center">
              <FileText className="w-4 h-4 text-slate-600" strokeWidth={1.5} />
            </div>
            <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Modules</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 font-heading">
            {certification?.completed_modules || 0}
            <span className="text-sm font-normal text-slate-400 ml-1">/ {certification?.total_modules || 22}</span>
          </div>
        </div>
        <div className="bg-white border border-slate-200 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] rounded-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-slate-100 rounded-sm flex items-center justify-center">
              <CheckSquare className="w-4 h-4 text-slate-600" strokeWidth={1.5} />
            </div>
            <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Assessments</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 font-heading">
            {modules.filter((m) => m.progress?.assessment_passed).length}
            <span className="text-sm font-normal text-slate-400 ml-1">/ {modules.length} passed</span>
          </div>
        </div>
        <div 
          className="bg-white border border-slate-200 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] rounded-sm p-5 cursor-pointer hover:border-[#005d6c]/30 transition-colors"
          onClick={() => navigate("/progression")}
          data-testid="promotion-card"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#005d6c]/10 rounded-sm flex items-center justify-center">
              <Star className="w-4 h-4 text-[#005d6c]" strokeWidth={1.5} />
            </div>
            <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Promotion Level</span>
          </div>
          <div className="text-2xl font-bold font-heading text-[#005d6c]">
            {promotionStatus ? `L${promotionStatus.current_level}: ${promotionStatus.current_title}` : "Loading..."}
          </div>
          {promotionStatus && (
            <div className="text-xs text-slate-500 mt-1">{READINESS_LABELS[promotionStatus.revenue_readiness] || promotionStatus.revenue_readiness}</div>
          )}
        </div>
        <div 
          className="bg-white border border-slate-200 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] rounded-sm p-5 cursor-pointer hover:border-amber-500/30 transition-colors"
          onClick={() => navigate("/progression")}
          data-testid="partner-track-card"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-amber-50 rounded-sm flex items-center justify-center">
              <Crown className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
            </div>
            <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Partner Track</span>
          </div>
          <div className={`text-lg font-bold font-heading ${partnerTrackStatus?.current_stage === 'not_eligible' ? 'text-slate-400' : 'text-amber-600'}`}>
            {partnerTrackStatus ? PARTNER_TRACK_STAGE_NAMES[partnerTrackStatus.current_stage] : "Loading..."}
          </div>
          {partnerTrackStatus?.validation_due && partnerTrackStatus.current_stage !== 'not_eligible' && (
            <div className="text-xs text-amber-600 mt-1 font-medium">Validation Due</div>
          )}
        </div>
        <div className="bg-white border border-slate-200 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] rounded-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-slate-100 rounded-sm flex items-center justify-center">
              <Award className="w-4 h-4 text-slate-600" strokeWidth={1.5} />
            </div>
            <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Certification</span>
          </div>
          <div className="text-2xl font-bold font-heading">
            {certification?.certified ? (
              <span className="text-emerald-600">Certified</span>
            ) : (
              <span className="text-slate-400">In Progress</span>
            )}
          </div>
        </div>
      </div>

      {/* Modules Grid */}
      <div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
          Core Modules — Executive Search Lifecycle
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {modules.filter(m => m.id <= 11).map((mod, idx) => {
            const Icon = ICON_MAP[mod.icon] || BookOpen;
            const progress = getModuleProgress(mod.progress || {});
            const isComplete = progress === 100;
            const isStarted = progress > 0;
            const isLocked = false; // All core modules are unlocked

            return (
              <button
                key={mod.id}
                data-testid={`module-card-${mod.id}`}
                onClick={() => navigate(`/modules/${mod.id}`)}
                disabled={isLocked}
                className={`text-left bg-white border shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-pointer rounded-sm p-5 animate-fade-in ${
                  isComplete ? "border-emerald-200" : "border-slate-200"
                } ${isLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                style={{ animationDelay: `${idx * 0.04}s` }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 rounded-sm flex items-center justify-center ${
                        isComplete ? "bg-emerald-50" : "bg-slate-100"
                      }`}
                    >
                      {isLocked ? (
                        <Lock className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                      ) : (
                        <Icon
                          className={`w-4 h-4 ${
                            isComplete ? "text-emerald-600" : "text-slate-600"
                          }`}
                          strokeWidth={1.5}
                        />
                      )}
                    </div>
                    <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
                      Module {mod.id}
                    </span>
                  </div>
                  {isComplete ? (
                    <Badge
                      variant="secondary"
                      className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"
                    >
                      Complete
                    </Badge>
                  ) : isLocked ? (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-500 border-slate-200 text-xs">
                      Locked
                    </Badge>
                  ) : null}
                </div>
                <h3 className="font-heading text-base font-semibold text-slate-900 mb-1.5">
                  {mod.title}
                </h3>
                <p className="text-sm text-slate-500 line-clamp-2 mb-3 font-body leading-relaxed">
                  {mod.description}
                </p>
                <div className="flex items-center justify-between mb-3">
                  <Progress value={progress} className="h-1 flex-1 mr-3" />
                  <span className="text-xs text-slate-400 font-medium whitespace-nowrap">
                    {progress}%
                  </span>
                </div>
                <div className={`flex items-center gap-2 text-xs font-medium ${
                  isComplete ? "text-emerald-600" : isStarted ? "text-[#005d6c]" : "text-slate-500"
                }`}>
                  {isComplete ? (
                    <>
                      <RotateCcw className="w-3 h-3" />
                      <span>Review</span>
                    </>
                  ) : isStarted ? (
                    <>
                      <Play className="w-3 h-3" />
                      <span>Resume</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-3 h-3" />
                      <span>Start</span>
                    </>
                  )}
                  <ChevronRight className="w-3 h-3 ml-auto" />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Phase 2 Modules */}
      {modules.filter(m => m.id > 11 && m.id <= 20).length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-1">
            Revenue, BD & Desk Building
          </h2>
          <p className="text-sm text-slate-500 mb-4 font-body">360-degree consultant capability — client + candidate + revenue ownership.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {modules.filter(m => m.id > 11 && m.id <= 20).map((mod, idx) => {
              const Icon = ICON_MAP[mod.icon] || BookOpen;
              const progress = getModuleProgress(mod.progress || {});
              const isComplete = progress === 100;
              const isStarted = progress > 0;

              return (
                <button
                  key={mod.id}
                  data-testid={`module-card-${mod.id}`}
                  onClick={() => navigate(`/modules/${mod.id}`)}
                  className={`text-left bg-white border shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-pointer rounded-sm p-5 animate-fade-in ${
                    isComplete ? "border-emerald-200" : "border-slate-200"
                  }`}
                  style={{ animationDelay: `${idx * 0.04}s` }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-sm flex items-center justify-center ${
                          isComplete ? "bg-emerald-50" : "bg-[#005d6c]/10"
                        }`}
                      >
                        <Icon
                          className={`w-4 h-4 ${
                            isComplete ? "text-emerald-600" : "text-[#005d6c]"
                          }`}
                          strokeWidth={1.5}
                        />
                      </div>
                      <span className="text-xs text-[#005d6c] uppercase tracking-wider font-semibold">
                        Module {mod.id}
                      </span>
                    </div>
                    {isComplete && (
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                        Complete
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-heading text-base font-semibold text-slate-900 mb-1.5">
                    {mod.title}
                  </h3>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-3 font-body leading-relaxed">
                    {mod.description}
                  </p>
                  <div className="flex items-center justify-between mb-3">
                    <Progress value={progress} className="h-1 flex-1 mr-3" />
                    <span className="text-xs text-slate-400 font-medium whitespace-nowrap">
                      {progress}%
                    </span>
                  </div>
                  <div className={`flex items-center gap-2 text-xs font-medium ${
                    isComplete ? "text-emerald-600" : isStarted ? "text-[#005d6c]" : "text-slate-500"
                  }`}>
                    {isComplete ? (
                      <>
                        <RotateCcw className="w-3 h-3" />
                        <span>Review</span>
                      </>
                    ) : isStarted ? (
                      <>
                        <Play className="w-3 h-3" />
                        <span>Resume</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-3 h-3" />
                        <span>Start</span>
                      </>
                    )}
                    <ChevronRight className="w-3 h-3 ml-auto" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Advanced Modules (21-22) */}
      {modules.filter(m => m.id > 20).length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-1">
            Advanced Modules
          </h2>
          <p className="text-sm text-slate-500 mb-4 font-body">Modern tools and commercial essentials for the complete search consultant.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {modules.filter(m => m.id > 20).map((mod, idx) => {
              const Icon = ICON_MAP[mod.icon] || Cpu;
              const progress = getModuleProgress(mod.progress || {});
              const isComplete = progress === 100;
              const isStarted = progress > 0;

              return (
                <button
                  key={mod.id}
                  data-testid={`module-card-${mod.id}`}
                  onClick={() => navigate(`/modules/${mod.id}`)}
                  className={`text-left bg-white border shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] hover:shadow-md hover:border-amber-400/50 transition-all duration-200 cursor-pointer rounded-sm p-5 animate-fade-in ${
                    isComplete ? "border-emerald-200" : "border-amber-200"
                  }`}
                  style={{ animationDelay: `${idx * 0.04}s` }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-sm flex items-center justify-center ${
                          isComplete ? "bg-emerald-50" : "bg-amber-50"
                        }`}
                      >
                        <Icon
                          className={`w-4 h-4 ${
                            isComplete ? "text-emerald-600" : "text-amber-600"
                          }`}
                          strokeWidth={1.5}
                        />
                      </div>
                      <span className="text-xs text-amber-600 uppercase tracking-wider font-semibold">
                        Module {mod.id}
                      </span>
                    </div>
                    {isComplete ? (
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                        Complete
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                        New
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-heading text-base font-semibold text-slate-900 mb-1.5">
                    {mod.title}
                  </h3>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-3 font-body leading-relaxed">
                    {mod.description}
                  </p>
                  <div className="flex items-center justify-between mb-3">
                    <Progress value={progress} className="h-1 flex-1 mr-3" />
                    <span className="text-xs text-slate-400 font-medium whitespace-nowrap">
                      {progress}%
                    </span>
                  </div>
                  <div className={`flex items-center gap-2 text-xs font-medium ${
                    isComplete ? "text-emerald-600" : isStarted ? "text-amber-600" : "text-slate-500"
                  }`}>
                    {isComplete ? (
                      <>
                        <RotateCcw className="w-3 h-3" />
                        <span>Review</span>
                      </>
                    ) : isStarted ? (
                      <>
                        <Play className="w-3 h-3" />
                        <span>Resume</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-3 h-3" />
                        <span>Start</span>
                      </>
                    )}
                    <ChevronRight className="w-3 h-3 ml-auto" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
